/**
 * The minimum-safety-equipment checklist (§4.3).
 *
 * NHRA's general regulations are a ladder: every tier a car steps down pulls in
 * more equipment, and the tiers are quarter-mile ET with a trap-speed override
 * beside them. The ladder this transcribes, quickest last:
 *
 *   14.00 and slower  a licence, belts, a working tail light
 *   13.99  helmet · driveshaft loop (unless 13.00-or-slower on street tires)
 *   13.49  roll bar and SFI belts, in a convertible
 *   11.99  steel valve stems · arm restraints in an open car
 *   11.49  6-point roll bar · SFI 16.1 belts · single-layer jacket
 *          · SFI 1.1/1.2 flywheel and SFI 6.x bellhousing behind a manual
 *   10.99  SFI 4.1 trans shield · SFI 18.1 balancer · aftermarket axles
 *          · locking dipstick tube                        (or 135 mph)
 *    9.99  roll cage · chassis certification · competition licence
 *          · SFI 3.2A/5 suit, gloves, neck collar · window net
 *          · SFI 29.1 flexplate and 30.1 shield behind an automatic  (or 135 mph)
 *    7.49  face shield · SFI 38.1 head-and-neck restraint
 *  150 mph  parachute · SFI 38.1
 *
 * And one rung that is not an ET at all: a supercharged or turbocharged car on
 * alcohol, at 9.99 or quicker, owes an SFI 3.2A/15 suit rather than the 3.2A/5
 * the same car would wear on gasoline — and its roots blower owes a mandatory
 * SFI 14.1 restraint at any pace. Fuel moves the ladder, which is why the form
 * asks what the thing burns.
 *
 * Two things this file is careful about.
 *
 * **Tiers, not opinions.** Every entry says which rule fires it, in the racer's
 * own units, because "required" with no reason attached is not checkable against
 * the rulebook — and checking it against the rulebook is the entire instruction
 * this tool gives.
 *
 * **The three grades are not decoration.** `required` means the general
 * regulations call for it at this pace. `class-dependent` means it turns on the
 * class rather than the ET, and this tool genuinely cannot know — a fuel cell is
 * the clearest case. `recommended` is neither: it is equipment nobody makes you
 * carry at this pace and everybody sensible carries anyway. Blurring the three
 * would make the required set unbelievable, which is the one thing it must not
 * be.
 *
 * It overlaps `flows/safety-kit.ts`, which pre-checks gear to *track* in a
 * garage. That one stays the authority for what SvcDue creates as items; this
 * one is the fuller read of the rulebook and admits grades and non-equipment
 * requirements it has no way to store. `test/classcalc.test.ts` pins them
 * together so the two can never tell a racer different things.
 */

import type { ChassisLevel, Fuel, PowerAdder, Tire, Transmission, VehicleType } from './spec';
import { OPEN_BODIED, hasCage, isAlcohol, isAutomatic, isManual, isPowerAdded } from './spec';

export type Tier = 'required' | 'recommended' | 'class-dependent';

export const CATEGORIES = [
  'Driver',
  'Chassis & restraint',
  'Driveline',
  'Fuel & electrical',
  'Credentials',
  'Drag-and-drive',
] as const;
export type Category = (typeof CATEGORIES)[number];

export interface SafetyContext {
  /** Quarter-mile ET, seconds. */
  et: number;
  /**
   * Trap speed, mph, or null when it is not known.
   *
   * Null means the speed-based half of a tier simply does not fire — never that
   * it fires as zero, and never that the whole rule is skipped. Every threshold
   * in the ladder is "ET or mph", so a racer with only an ET still gets the ET
   * half of every one of them.
   */
  mph: number | null;
  vehicleType: VehicleType;
  transmission: Transmission;
  tire: Tire;
  powerAdder: PowerAdder;
  /**
   * What it burns.
   *
   * One value on this list moves the ladder rather than sitting beside it: a
   * blown or turbocharged car on methanol at 9.99 or quicker is owed two more
   * layers of suit than the same car on gasoline, and its roots blower picks up
   * a mandatory restraint. See `isAlcohol` in spec.ts for why E85 is not folded
   * in with it.
   */
  fuel: Fuel;
  chassis: ChassisLevel;
  batteryRelocated: boolean;
  /**
   * The racer is looking at Drag Week / Sick Week.
   *
   * Both events run the NHRA general regulations unchanged — "all vehicles must
   * meet NHRA safety rules for the e.t. and speed they are capable of" — so the
   * whole ladder above applies as-is. What they add is a short list of their own
   * that has no equivalent in sanctioned racing, because the car has to drive
   * eight hundred-odd miles of public road between the tracks.
   */
  dragAndDrive: boolean;
}

export interface SafetyItem {
  key: string;
  label: string;
  category: Category;
  /** The SFI / Snell spec the piece has to carry, where one applies. */
  spec: string | null;
  /**
   * The grade at this pace, or null when the item simply does not apply to this
   * machine — an automatic's flexplate shield on a clutch car, say. Null items
   * are dropped rather than listed as "not required", which would bury the list.
   */
  grade: (ctx: SafetyContext) => { tier: Tier; why: string } | null;
}

/** An unknown trap speed fires nothing — it never counts as zero or as fast. */
const atLeast = (mph: number | null, limit: number) => mph !== null && mph >= limit;

/** The 9.99-or-135-mph line, which pulls in more than any other single tier. */
const CAGE_TIER = (ctx: SafetyContext) => ctx.et <= 9.99 || atLeast(ctx.mph, 135);
const CAGE_WHY = '9.99 or quicker, or 135 mph and up';

/**
 * The rule that puts two more layers on the driver.
 *
 * "9.99 and faster, any vehicle, supercharged or turbocharged with alcohol:
 * Suit meeting SFI Spec 3.2A/15, gloves and boots meeting SFI Spec 3.3/5
 * mandatory" — NHRA general regulations, protective equipment.
 *
 * Note what it turns on: alcohol *and* boost, not alcohol alone. A naturally
 * aspirated methanol car is not in it, and saying otherwise would be inventing
 * a rule rather than reading one.
 */
const BLOWN_ALCOHOL = (ctx: SafetyContext) =>
  isAlcohol(ctx.fuel) &&
  (ctx.powerAdder === 'Supercharger — roots / screw' ||
    ctx.powerAdder === 'Supercharger — centrifugal' ||
    ctx.powerAdder === 'Single turbo' ||
    ctx.powerAdder === 'Twin turbo' ||
    ctx.powerAdder === 'Combination (adder + nitrous)');

const req = (why: string) => ({ tier: 'required' as const, why });
const rec = (why: string) => ({ tier: 'recommended' as const, why });
const dep = (why: string) => ({ tier: 'class-dependent' as const, why });

export const SAFETY_ITEMS: SafetyItem[] = [
  // --- credentials ---------------------------------------------------------
  {
    key: 'licence',
    label: "Driver's licence or competition licence",
    category: 'Credentials',
    spec: null,
    grade: () => req('every competitor, at every pace'),
  },
  {
    key: 'complicence',
    label: 'NHRA competition licence',
    category: 'Credentials',
    spec: null,
    grade: (ctx) => (CAGE_TIER(ctx) ? req(CAGE_WHY) : null),
  },
  {
    key: 'chassiscert',
    label: 'NHRA / SFI chassis certification',
    category: 'Credentials',
    spec: 'SFI 25.x tag · sportsman tags run 3 years',
    /**
     * The one place the cage rule and the certification rule do not line up.
     *
     * `CAGE_TIER` is "9.99 or quicker, or 135 mph and up", and a cage really is
     * required on both halves of it. The sentence that requires the serialised
     * *tag*, though, is written on one half only — "9.99 or quicker e.t.
     * chassis" (general regulations 4:11). So a 10.30 car that trips 137 owes a
     * cage beyond doubt and owes a sticker beyond this page's knowledge, and
     * calling that `required` would be inventing the half of the rule that
     * isn't there. It grades `class-dependent` instead, which is this file's
     * existing word for "the rulebook does not settle it and neither will we" —
     * the row still appears, with the question on it. `credentials.ts` gives
     * the same case its `ask` standing and the division office's number.
     */
    grade: (ctx) => {
      if (!CAGE_TIER(ctx)) return null;
      if (ctx.et > 9.99) return dep('a cage is required over 135 mph — whether it must also be tagged at an ET slower than 10.00 is your division office’s call');
      return req(CAGE_WHY + ' — inspected and tagged');
    },
  },
  {
    key: 'memberlicence',
    label: 'Track or series membership',
    category: 'Credentials',
    spec: null,
    grade: () => dep('set by the series you enter, not by your ET'),
  },

  // --- driver --------------------------------------------------------------
  {
    key: 'helmet',
    label: 'Helmet',
    category: 'Driver',
    spec: 'Snell SA2020 / SA2025 · SA2015 expires 1 Jan 2027',
    grade: (ctx) => {
      if (CAGE_TIER(ctx)) return req(CAGE_WHY + ' — full-face');
      if (ctx.et <= 13.99) return req('13.99 or quicker (full-face in an open car)');
      return rec('not required above 13.99, and the cheapest insurance on the property');
    },
  },
  {
    key: 'faceshield',
    label: 'Helmet face shield',
    category: 'Driver',
    spec: null,
    grade: (ctx) => (ctx.et <= 7.49 || atLeast(ctx.mph, 150) ? req('7.49 or quicker, or 150 mph and up') : null),
  },
  {
    key: 'jacket',
    label: 'Fire jacket — single layer',
    category: 'Driver',
    spec: 'SFI 3.2A/1',
    grade: (ctx) => {
      // Superseded by the full suit below — never list both.
      if (CAGE_TIER(ctx)) return null;
      if (ctx.et <= 11.49) return req('11.49 or quicker');
      if (ctx.et <= 13.99 && isPowerAdded(ctx.powerAdder)) return req('11.50–13.99 with a non-OEM power adder');
      if (ctx.et <= 13.99) return rec('not required at this pace naturally aspirated');
      return null;
    },
  },
  {
    key: 'suit',
    label: 'Fire suit — jacket & pants',
    category: 'Driver',
    spec: 'SFI 3.2A/5',
    grade: (ctx) => {
      // Superseded by the alcohol suit below, exactly as the single-layer jacket
      // is superseded by this one. Never list two suits.
      if (ctx.et <= 9.99 && BLOWN_ALCOHOL(ctx)) return null;
      return CAGE_TIER(ctx) ? req(CAGE_WHY) : null;
    },
  },
  {
    key: 'suit-alcohol',
    label: 'Fire suit — jacket & pants',
    category: 'Driver',
    spec: 'SFI 3.2A/15',
    grade: (ctx) =>
      ctx.et <= 9.99 && BLOWN_ALCOHOL(ctx)
        ? req('9.99 or quicker, supercharged or turbocharged on alcohol — two layers past the 3.2A/5 the same car runs on gasoline')
        : null,
  },
  {
    key: 'gloves',
    label: 'Gloves',
    category: 'Driver',
    spec: 'SFI 3.3/5',
    grade: (ctx) => (CAGE_TIER(ctx) ? req(CAGE_WHY) : null),
  },
  {
    key: 'shoes',
    label: 'Shoes',
    category: 'Driver',
    spec: 'SFI 3.3/5',
    grade: (ctx) => (CAGE_TIER(ctx) ? req(CAGE_WHY) : null),
  },
  {
    key: 'neckcollar',
    label: 'Neck collar',
    category: 'Driver',
    spec: 'SFI 3.3',
    grade: (ctx) => {
      // The 38.1 device supersedes the collar, so the two never both appear.
      if (ctx.et <= 7.49 || atLeast(ctx.mph, 150)) return null;
      return CAGE_TIER(ctx) ? req(CAGE_WHY + ', unless an SFI 38.1 device is worn') : null;
    },
  },
  {
    key: 'hans',
    label: 'Head & neck restraint',
    category: 'Driver',
    spec: 'SFI 38.1',
    grade: (ctx) => {
      if (ctx.et <= 7.49 || atLeast(ctx.mph, 150)) return req('7.49 or quicker, or 150 mph and up');
      return CAGE_TIER(ctx) ? rec('not required until 7.49 / 150 mph — and it is the piece that saves necks') : null;
    },
  },
  {
    key: 'harness',
    label: 'Driver harness',
    category: 'Driver',
    spec: 'SFI 16.1 · recertify every 2 years',
    grade: (ctx) => {
      if (ctx.et <= 11.49 || atLeast(ctx.mph, 135)) return req('11.49 or quicker, or any car with a bar or cage');
      if (ctx.vehicleType === 'Convertible' && ctx.et <= 13.49) return req('13.49 or quicker in a convertible');
      return null;
    },
  },
  {
    key: 'armrestraints',
    label: 'Arm restraints',
    category: 'Driver',
    spec: null,
    grade: (ctx) => {
      if (!OPEN_BODIED.includes(ctx.vehicleType)) {
        return CAGE_TIER(ctx) ? dep('full-bodied cars use a window net instead') : null;
      }
      return ctx.et <= 11.99 ? req('11.99 or quicker in an open-bodied car') : null;
    },
  },

  // --- chassis & restraint -------------------------------------------------
  {
    key: 'rollbar',
    label: 'Roll bar — 6-point',
    category: 'Chassis & restraint',
    spec: 'NHRA general regulations',
    grade: (ctx) => {
      // The cage supersedes the bar; only one of the pair is ever listed.
      if (CAGE_TIER(ctx)) return null;
      if (ctx.et <= 11.49) return req('10.00–11.49');
      if (ctx.vehicleType === 'Convertible' && ctx.et <= 13.49) return req('13.49 or quicker in a convertible');
      return null;
    },
  },
  {
    key: 'rollcage',
    label: 'Roll cage',
    category: 'Chassis & restraint',
    spec: 'Certified · 3-year recertification',
    grade: (ctx) => (CAGE_TIER(ctx) ? req(CAGE_WHY) : null),
  },
  {
    key: 'windownet',
    label: 'Window net',
    category: 'Chassis & restraint',
    spec: 'SFI 27.1',
    grade: (ctx) => {
      if (OPEN_BODIED.includes(ctx.vehicleType)) return null;
      if (CAGE_TIER(ctx)) return req(CAGE_WHY + ' in a full-bodied car');
      if (ctx.et <= 10.99 && hasCage(ctx.chassis)) return dep('10.99 with a cage and altered floor pans');
      return null;
    },
  },
  {
    key: 'seat',
    label: 'Racing seat, mounted to the structure',
    category: 'Chassis & restraint',
    spec: null,
    grade: (ctx) => (CAGE_TIER(ctx) ? dep('specified per class alongside the cage rules') : null),
  },
  {
    key: 'parachute',
    label: 'Parachute',
    category: 'Chassis & restraint',
    spec: null,
    grade: (ctx) => {
      if (atLeast(ctx.mph, 150)) return req('150 mph and up');
      if (atLeast(ctx.mph, 140)) return rec('you are one good run from the 150 mph rule');
      return null;
    },
  },
  {
    key: 'wheeliebars',
    label: 'Wheelie bars',
    category: 'Chassis & restraint',
    spec: null,
    grade: () => dep('never required — and banned outright in some classes'),
  },

  // --- driveline -----------------------------------------------------------
  {
    key: 'driveshaftloop',
    label: 'Driveshaft loop',
    category: 'Driveline',
    spec: null,
    grade: (ctx) => {
      if (ctx.vehicleType === 'Dragster') return null;
      // 13.99 and quicker, with the street-tire car at 13.00 or slower let off.
      if (ctx.et > 13.99) return null;
      if (ctx.et >= 13.0 && ctx.tire === 'Street tire (DOT)') {
        return rec('13.00-or-slower street-tire cars are excepted');
      }
      return req('13.99 or quicker');
    },
  },
  {
    key: 'transshield',
    label: 'Transmission shield',
    category: 'Driveline',
    spec: 'SFI 4.1',
    grade: (ctx) => {
      if (!isAutomatic(ctx.transmission)) return null;
      return ctx.et <= 10.99 || atLeast(ctx.mph, 135) ? req('10.99 or quicker, or 135 mph and up') : null;
    },
  },
  {
    key: 'flexplate',
    label: 'Flexplate & flexplate shield',
    category: 'Driveline',
    spec: 'SFI 29.1 flexplate · SFI 30.1 shield',
    grade: (ctx) => {
      if (!isAutomatic(ctx.transmission)) return null;
      return CAGE_TIER(ctx) ? req(CAGE_WHY + ' behind an automatic') : null;
    },
  },
  {
    key: 'dipstick',
    label: 'Locking transmission dipstick tube',
    category: 'Driveline',
    spec: null,
    grade: (ctx) => {
      if (!isAutomatic(ctx.transmission)) return null;
      return ctx.et <= 10.99 ? req('10.99 or quicker') : null;
    },
  },
  {
    key: 'bellhousing',
    label: 'Flywheel, clutch & bellhousing',
    category: 'Driveline',
    spec: 'SFI 1.1 / 1.2 flywheel · SFI 6.1 / 6.2 / 6.3 / 9.1 bellhousing',
    grade: (ctx) => {
      if (!isManual(ctx.transmission)) return null;
      return ctx.et <= 11.49 ? req('11.49 or quicker behind a clutch') : null;
    },
  },
  {
    key: 'balancer',
    label: 'Harmonic balancer',
    category: 'Driveline',
    spec: 'SFI 18.1',
    grade: (ctx) => (ctx.et <= 10.99 ? req('10.99 or quicker') : null),
  },
  {
    key: 'axles',
    label: 'Aftermarket axles & axle retainers',
    category: 'Driveline',
    spec: null,
    grade: (ctx) => (ctx.et <= 10.99 ? req('10.99 or quicker') : null),
  },
  {
    key: 'valvestems',
    label: 'Steel valve stems',
    category: 'Driveline',
    spec: null,
    grade: (ctx) => (ctx.et <= 11.99 ? req('11.99 or quicker') : null),
  },
  {
    key: 'blowerrestraint',
    label: 'Blower restraint & burst panel',
    category: 'Driveline',
    spec: 'SFI 14.1 / 14.2 restraint · SFI 23.1 manifold burst panel',
    grade: (ctx) => {
      const blown = ctx.powerAdder === 'Supercharger — roots / screw' || ctx.powerAdder === 'Combination (adder + nitrous)';
      if (!blown) return null;
      // "Supercharger restraint system meeting SFI Spec 14.1 mandatory on
      // Roots-type supercharger when alcohol is used as fuel." On gasoline the
      // same blower is a class-by-class matter; the fuel is what makes it a rule.
      if (isAlcohol(ctx.fuel)) return req('a Roots-type blower on alcohol — mandatory, whatever the ET');
      return dep('specified per class for roots and screw blowers — a screw blower also needs an SFI 23.1 manifold burst panel');
    },
  },

  // --- fuel & electrical ---------------------------------------------------
  {
    key: 'mastercutoff',
    label: 'Master electrical cutoff',
    category: 'Fuel & electrical',
    spec: 'Rear-mounted, clearly marked',
    grade: (ctx) => {
      if (CAGE_TIER(ctx)) return req(CAGE_WHY);
      if (ctx.batteryRelocated) return req('any car with the battery moved from its factory location');
      return null;
    },
  },
  {
    key: 'batterybox',
    label: 'Battery box & secure mounting',
    category: 'Fuel & electrical',
    spec: null,
    grade: (ctx) => {
      if (ctx.batteryRelocated) return req('any relocated battery — boxed, vented and strapped');
      return CAGE_TIER(ctx) ? dep('specified per class where the battery has moved') : null;
    },
  },
  {
    key: 'fuelcell',
    label: 'Fuel cell',
    category: 'Fuel & electrical',
    spec: 'SFI 28.1 bladder, where specified',
    grade: (ctx) =>
      ctx.et <= 7.49
        ? req('7.49 or quicker')
        : dep('turns on the class and the fuel, not on ET alone — the common trigger'),
  },
  {
    key: 'fuellines',
    label: 'Fuel lines routed outside the cockpit',
    category: 'Fuel & electrical',
    spec: null,
    grade: (ctx) => (CAGE_TIER(ctx) ? req(CAGE_WHY + ' — shielded where they pass the driver') : null),
  },
  {
    key: 'firesystem',
    label: 'Onboard fire suppression',
    category: 'Fuel & electrical',
    spec: 'SFI 17.1',
    grade: (ctx) => {
      if (ctx.et <= 7.49) return dep('specified per class at this pace, and near-universal in practice');
      return CAGE_TIER(ctx) ? rec('not required by ET — cheap next to what it protects') : null;
    },
  },

  // --- drag-and-drive ------------------------------------------------------
  //
  // Only shown when the racer is looking at Drag Week / Sick Week. The two
  // events genuinely differ here, so both numbers are named rather than one
  // averaged answer that is wrong for whichever event you actually entered.
  {
    key: 'dnd-streetlegal',
    label: 'Registration, insurance and plate',
    category: 'Drag-and-drive',
    spec: 'Paperwork must match the VIN and the plate on the car',
    grade: (ctx) =>
      ctx.dragAndDrive
        ? req('every entry, both events — no dealer or manufacturer plates, and no rental cars')
        : null,
  },
  {
    key: 'dnd-lights',
    label: 'Headlights, taillights, brake lights, turn signals, horn, mirror',
    category: 'Drag-and-drive',
    spec: null,
    grade: (ctx) => (ctx.dragAndDrive ? req('all functional — checked at tech, and you drive the route on them') : null),
  },
  {
    key: 'dnd-diaper',
    label: 'Engine diaper / fluid containment',
    category: 'Drag-and-drive',
    spec: null,
    grade: (ctx) => {
      if (!ctx.dragAndDrive) return null;
      // Drag Week 8.99; Sick Week 9.99 required and 11.49 recommended.
      if (ctx.et <= 8.99) return req('Drag Week: 8.99 and quicker. Sick Week: 9.99 and quicker');
      if (ctx.et <= 9.99) return req('Sick Week: 9.99 and quicker. Drag Week does not require it until 8.99');
      if (ctx.et <= 11.49) return rec('Sick Week strongly recommends one at 11.49 and quicker');
      return null;
    },
  },
  {
    key: 'dnd-tires',
    label: 'Purpose-built drag tires for the track',
    category: 'Drag-and-drive',
    spec: 'Slicks, DOT slicks or drag radials',
    grade: (ctx) =>
      ctx.dragAndDrive
        ? req('no conventional treaded street tires on the strip — tire rules apply on track only, not on the road')
        : null,
  },
  {
    key: 'dnd-onelongblock',
    label: 'One long-block for the whole week',
    category: 'Drag-and-drive',
    spec: null,
    grade: (ctx) =>
      ctx.dragAndDrive
        ? req('swapping a track engine for a road engine is not allowed; a replacement needs race-director approval')
        : null,
  },
  {
    key: 'dnd-fuel',
    label: 'The NHRA equipment for the fuel you are running',
    category: 'Drag-and-drive',
    spec: null,
    grade: (ctx) => {
      if (!ctx.dragAndDrive || !isAlcohol(ctx.fuel)) return null;
      // Sick Week states it outright, and it is the whole reason the fuel
      // question is on this page: "Vehicles in all classes may use any fuel
      // provided the car and the driver have the required safety equipment
      // required by the NHRA for that fuel type."
      return req('any fuel is allowed, on the condition that the car and driver carry what NHRA requires for it — on methanol that is a longer list, and it is checked at tech');
    },
  },
  {
    key: 'dnd-licence',
    label: 'NHRA / IHRA competition licence for your teched ET',
    category: 'Drag-and-drive',
    spec: null,
    grade: (ctx) =>
      ctx.dragAndDrive
        ? req('officials will not accept a slip quicker than the car or the driver is teched for')
        : null,
  },
];

export interface GradedItem extends Omit<SafetyItem, 'grade'> {
  tier: Tier;
  why: string;
}

/** Every requirement that applies to this machine, in catalogue order. */
export function checklist(ctx: SafetyContext): GradedItem[] {
  const out: GradedItem[] = [];
  for (const item of SAFETY_ITEMS) {
    const graded = item.grade(ctx);
    if (!graded) continue;
    const { grade: _drop, ...rest } = item;
    out.push({ ...rest, tier: graded.tier, why: graded.why });
  }
  return out;
}

/** The checklist split by category, dropping categories with nothing in them. */
export function checklistByCategory(ctx: SafetyContext): { category: Category; items: GradedItem[] }[] {
  const graded = checklist(ctx);
  return CATEGORIES.map((category) => ({
    category,
    items: graded.filter((i) => i.category === category),
  })).filter((group) => group.items.length > 0);
}

export function tierCounts(items: GradedItem[]): Record<Tier, number> {
  return {
    required: items.filter((i) => i.tier === 'required').length,
    recommended: items.filter((i) => i.tier === 'recommended').length,
    'class-dependent': items.filter((i) => i.tier === 'class-dependent').length,
  };
}
